/*
 *  			'License for ONE AXA SOFTWARE - Inner Source'
 * 					L O A S - I S
 * 
 * The following License aims to be applied between the AXA Companies, according to the following definition:
 * "AXA Companies" means AXA, Société Anonyme with a “AXA Companies ” Board of Directors having its principal offices at 25, avenue Matignon, 75008 Paris, registered on the Commercial Registry of Paris under the number 572 093 920; and (i) any other company controlled by, or controlling AXA, with a company being considered as controlling another: (a) when it holds directly or indirectly a portion of the capital according to it the majority of the voting rights in general meetings of shareholders of this company; (b) when it holds solely the majority of the voting rights in this company by virtue of an agreement concluded with other partners or shareholders and which is not contrary to the interest of the company; (c) when it determines de facto, by voting rights which it holds, the decisions in the general meetings of shareholders of this company; (d) in any event, when it holds, directly or indirectly, a portion of voting rights greater than 40% and when no other partner or shareholder holds directly or indirectly a portion which is greater than its own; (ii) any economic interest group in which AXA and/or one or more other Companies of the AXA Group participates for at least 50% in operating costs; (iii) in the cases where the law applicable to a company limits voting rights or control (such as defined here in above), this company will be deemed to be a company of the AXA Group, if the voting rights in 2 Binding Corporate Rules – AXA Group – version 2013 general shareholders’ meetings or the control held by a Company of the AXA Group reaches the maximum amount fixed by said applicable law; and (iv) all AXA Companies constitute the “AXA Group” (hereafter "One AXA").  (Binding Corporate Rules - AXA Group - version 2019).
 * 
 * Software credits: 
 * 	Creator: AXA GO GETD
 * 	Main contributors: AXA GO GETD
 * 
 * Permission is hereby granted, free of charge, to any person or entity within One AXA obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software within One AXA, including the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:
 * 
 * The above credit notice and this permission notice shall be included in all copies or substantial portions of the Software.
 * Any improvement or modification of the Software shall be made available to the whole One AXA, under the terms of this License.
 * 
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */

/**
 * ### Intents Table
 * ![header](../images/frontend/improve-conversation/by-intent.png)
 * #### External libraries
 * [Ant Design Table](https://ant.design/components/table/)
 *  @function
 * @memberOf ConversationHistory
 * @name ConversationHistory-IntentsPage
 */

import React from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Table, Button } from 'antd';
import { usePaginationWrapper } from '@hooks/usePaginationWrapper';
import { useQuery } from '@utils/locationHelper.js';
import { parseJson } from '@utils/toolsHelper.js';
import { getCustomNoData } from '@utils/noDataCustom';
import DSPage from '@design-system/page/DSPage';
import { FiltersArea } from '@components/commons/filtersArea/filtersArea';
import { MESSAGES } from '@translations/labels';
import { NONE_INTENT } from '@constants';
import { ConversationHistoryHeader } from '../conversationHistoryHeader';
import { useGetFilteredHistoryByIntent } from '../hooks/useGetFilteredHistoryByIntent';
import { useGetFilteredHistoryByIntentCbk } from '../hooks/useGetFilteredHistoryByIntentCbk';

const compareDesc = (a, b) => {
  if (a.counter > b.counter) {
    return -1;
  }
  if (b.counter > a.counter) {
    return 1;
  }
  return 0;
};

const labels = { ...MESSAGES.conversationHistory };

const ByIntentPage = () => {
  const { paginationWrapper } = usePaginationWrapper();
  const q = useQuery().get('q');
  const analyticCase = useQuery().get('analyticCase');
  const urlQuery = parseJson(q);

  const query = { ...urlQuery, isRemoteDataSource: true };
  if (analyticCase) {
    query.analyticCase = analyticCase;
  }
  useGetFilteredHistoryByIntent(query);
  const { getFilteredHistoryByIntentCbk } = useGetFilteredHistoryByIntentCbk(query);
  const rawMessages = useSelector(state => state.conversations.allByIntent);

  const intents =
    rawMessages && rawMessages.length
      ? [...rawMessages].filter(item => item.name.toLowerCase() !== NONE_INTENT).sort(compareDesc)
      : [];

  const generateLinkQuery = intentData => ({
    'nlp.intent': intentData.name,
    'nlp.locale': intentData.locale,
    isRemoteDataSource: true,
  });

  const columns = [
    {
      title: () => <span className="bold uppercase">{labels.tableIntentName}</span>,
      key: 'action',
      fixed: true,
      render: record => {
        const { name, locale, language } = record;

        return (
          <Button size="small" type="ghost" style={{ border: 'none' }}>
            <Link
              to={`/manage-the-bot/intents/${name}/name?locale=${locale || language}`}
              className="link-secondary block"
            >
              {name}
            </Link>
          </Button>
        );
      },
    },
    {
      title: <span className="bold uppercase">{labels.tableDomainName}</span>,
      dataIndex: 'domain',
      key: 'domain',
      fixed: true,
      width: '25%',
      align: 'center',
    },
    {
      title: <span className="bold uppercase">{labels.tableLanguage}</span>,
      dataIndex: 'language',
      key: 'language',
      fixed: true,
      width: '25%',
      align: 'center',
    },
    {
      title: <span className="bold uppercase">{labels.tableMostRepeated}</span>,
      key: 'id',
      fixed: true,
      width: '15%',
      align: 'right',
      render: record =>
        record.counter > 0 ? (
          <a href={`/conversation-history/by-message?q=${JSON.stringify(generateLinkQuery(record))}`}>
            {`${record.counter} ${labels.occurrences}`}
          </a>
        ) : (
          <>{`${record.counter} ${labels.occurrences}`}</>
        ),
    },
  ];

  return (
    <div className="conversation-history-content intents overflow-y-auto">
      <ConversationHistoryHeader />

      <DSPage>
        <header className="flex space-between">
          <h4 className="uppercase">{labels.byIntentSubtitle}</h4>
        </header>
        {(!!analyticCase || !!Object.keys(urlQuery).length) && (
          <FiltersArea
            analyticCase={analyticCase}
            occurrences={intents.length}
            filters={{ fromDate: urlQuery.fromDate, toDate: urlQuery.toDate }}
            callback={getFilteredHistoryByIntentCbk}
          />
        )}
        <Table
          locale={getCustomNoData()}
          columns={columns}
          loading={!intents}
          dataSource={intents}
          rowKey="id"
          pagination={paginationWrapper}
        />
      </DSPage>
    </div>
  );
};

export { ByIntentPage };
